import { useEffect, useRef } from 'react';
import { authApi } from '../api/auth.api';
import { useAuthStore } from '../../stores/authStore';

/** Refresh access token when it expires within this many seconds. */
const REFRESH_THRESHOLD_SECONDS = 5 * 60; // 5 minutes

/** Check interval in ms. */
const CHECK_INTERVAL_MS = 60 * 1000; // 1 minute

/**
 * Hook that proactively refreshes the access token when it is close to expiry.
 * Runs a timer while the user is authenticated; on success updates auth store token.
 * Use once at app root (e.g. in App.tsx).
 */
export function useTokenRefresh(): void {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const setToken = useAuthStore(state => state.setToken);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const checkAndRefresh = (): void => {
      if (!authApi.isAuthenticated()) return;
      if (!authApi.isAccessTokenExpiringWithin(REFRESH_THRESHOLD_SECONDS)) return;

      authApi.refreshToken().then(success => {
        if (success) {
          const newToken = authApi.getAccessToken();
          setToken(newToken);
        }
      });
    };

    checkAndRefresh();
    intervalRef.current = setInterval(checkAndRefresh, CHECK_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, setToken]);
}
